import type { BillingAddressFormData } from '@/lib/checkout';
import type { RepairReturnMethod } from '@/lib/service/ecs/logisticsCapabilities';
import type {
  RepairDetails,
  ServiceAddress,
  UpdateRepairRequest,
} from '@/lib/service/ecs/contracts';
import {
  formatRepairContactPhone,
  mapStoredToServiceAddress,
} from '@/lib/service/repairRequestReview/mapStoredToServiceAddress';

export type RepairDetailsUpdateKind =
  | 'returnMethod'
  | 'shippingAddress'
  | 'billingAddress'
  | 'contactInfo';

export interface RepairDetailsGuestAccess {
  email: string;
  accessCode: string;
}

export interface RepairDetailsReturnMethodValues {
  returnMethod: RepairReturnMethod;
  pickupBoutiqueId: string;
}

export interface RepairDetailsContactValues {
  email: string;
  phonePrefixCode: string;
  phone: string;
}

export interface BuildRepairUpdateRequestInput {
  repair: RepairDetails;
  kind: RepairDetailsUpdateKind;
  guestAccess?: RepairDetailsGuestAccess;
  returnMethod?: RepairDetailsReturnMethodValues;
  address?: BillingAddressFormData;
  contact?: RepairDetailsContactValues;
}

function withGuestAccess(
  request: UpdateRepairRequest,
  guestAccess?: RepairDetailsGuestAccess
): UpdateRepairRequest {
  if (!guestAccess) return request;
  return {
    ...request,
    guestAccess: {
      email: guestAccess.email,
      accessCode: guestAccess.accessCode,
    },
  };
}

function mapAddressFormToServiceAddress(
  address: BillingAddressFormData,
  email: string,
  phone: string,
  addressType: 'shipping' | 'billing'
): ServiceAddress {
  return mapStoredToServiceAddress(
    {
      title: '',
      firstName: address.firstName,
      lastName: address.lastName,
      companyName: address.companyName,
      streetAddress1: address.streetAddress1,
      streetAddress2: address.streetAddress2,
      city: address.city,
      postalCode: address.postalCode,
      countryArea: address.countryArea,
      country: address.country,
    },
    email,
    phone,
    addressType
  );
}

export function buildRepairUpdateRequest({
  repair,
  kind,
  guestAccess,
  returnMethod,
  address,
  contact,
}: BuildRepairUpdateRequestInput): UpdateRepairRequest {
  const base = { repairNumber: repair.repairNumber };

  switch (kind) {
    case 'returnMethod': {
      if (!returnMethod) throw new Error('returnMethod values are required');
      return withGuestAccess(
        {
          ...base,
          deliveryOption: returnMethod.returnMethod,
          ...(returnMethod.returnMethod === 'boutiquePickup'
            ? { pickupBoutiqueTracking: returnMethod.pickupBoutiqueId }
            : {}),
        },
        guestAccess
      );
    }
    case 'shippingAddress': {
      if (!address) throw new Error('address values are required');
      const phone = repair.shippingAddress.phoneNumber || repair.shippingAddress.mobileNumber || '';
      return withGuestAccess(
        {
          ...base,
          shippingAddress: mapAddressFormToServiceAddress(
            address,
            repair.shippingAddress.email,
            phone,
            'shipping'
          ),
        },
        guestAccess
      );
    }
    case 'billingAddress': {
      if (!address) throw new Error('address values are required');
      const phone = repair.billingAddress.phoneNumber || repair.billingAddress.mobileNumber || '';
      return withGuestAccess(
        {
          ...base,
          billingAddress: mapAddressFormToServiceAddress(
            address,
            repair.billingAddress.email,
            phone,
            'billing'
          ),
        },
        guestAccess
      );
    }
    case 'contactInfo': {
      if (!contact) throw new Error('contact values are required');
      const phone = formatRepairContactPhone(contact.phonePrefixCode, contact.phone);
      const contactFields = {
        email: contact.email,
        phoneNumber: phone,
        mobileNumber: phone,
      };
      return withGuestAccess(
        {
          ...base,
          shippingAddress: {
            ...repair.shippingAddress,
            ...contactFields,
          },
          billingAddress: {
            ...repair.billingAddress,
            ...contactFields,
          },
        },
        guestAccess
      );
    }
    default:
      throw new Error(`Unsupported repair update kind: ${kind satisfies never}`);
  }
}
